<template>
  <section v-if="session.isAuthenticated" class="workspace">
    <div v-if="currentError" class="workspace-alert">{{ currentError }}</div>
    <div v-if="!workspace.workspace" class="workspace-empty">Loading workspace</div>
    <div v-else :class="['workspace-editor', { 'workspace-editor-with-panel': commentsPanelOpen }]">
      <div :class="editorGridClass">
        <section
          v-for="pane in workspace.visiblePanes"
          :key="pane.paneIndex"
          :class="paneCssClass(pane)"
          @pointerdown.capture="activatePane(pane.paneIndex)"
          @focusin="activatePane(pane.paneIndex)"
          @click="activatePane(pane.paneIndex)"
          @dragenter="dragEnterPane(pane.paneIndex, $event)"
          @dragover="dragOverPane(pane.paneIndex, $event)"
          @dragleave="dragLeavePane(pane.paneIndex, $event)"
          @drop="dropDocumentOnPane(pane.paneIndex, $event)"
        >
        <div class="pane-bar">
          <template v-if="pane.paneIndex === 0">
            <button
              type="button"
              class="icon-button sidebar-toggle desktop-sidebar-toggle pane-sidebar-toggle"
              :title="sidebar.sidebarHidden.value ? 'Show sidebar' : 'Hide sidebar'"
              @click.stop="sidebar.toggleHidden()"
            >
              <PanelLeft class="icon" />
            </button>
            <button
              type="button"
              class="icon-button sidebar-toggle tablet-sidebar-toggle pane-sidebar-toggle"
              :title="sidebar.sidebarOpen.value ? 'Hide sidebar' : 'Show sidebar'"
              @click.stop="sidebar.toggleOpen()"
            >
              <PanelLeft class="icon" />
            </button>
          </template>
          <div class="pane-tabs">
            <div
              v-for="tab in pane.tabs"
              :key="tab.documentId"
              :class="tabClass(pane, tab.documentId)"
              @mouseup="closeTabOnMiddleClick($event, pane.paneIndex, tab.documentId)"
            >
              <button
                type="button"
                class="pane-tab-title"
                :data-title="tab.title"
                @click.stop="activateDocument(pane.paneIndex, tab.documentId)"
              >
                <FileText class="icon pane-tab-icon" />
                <span>{{ tab.title }}</span>
              </button>
              <button type="button" class="tab-close" title="Close document" @click.stop="closeDocument(pane.paneIndex, tab.documentId)">
                <X class="icon" />
              </button>
            </div>
          </div>
          <div v-if="activeDoc(pane) && showSplitButton(pane)" class="pane-bar-tools">
            <button
              v-if="showCommentsButton(pane)"
              type="button"
              class="icon-button pane-tool"
              :class="{ active: commentsPanelOpen }"
              :title="commentsPanelOpen ? 'Close comments' : 'Comments'"
              @click.stop="toggleCommentsPanel(pane)"
            >
              <MessageSquare class="icon" />
            </button>
            <button
              type="button"
              class="icon-button pane-tool"
              :title="splitTitle"
              @click.stop="toggleSplit(pane)"
            >
              <PanelRightClose v-if="workspace.visiblePanes.length > 1" class="icon" />
              <Columns2 v-else class="icon" />
            </button>
          </div>
        </div>
        <div class="pane-editor">
          <template v-if="pane.activeDocumentId && workspace.documents[pane.activeDocumentId]">
            <header class="doc-header">
              <div class="doc-header-text">
                <h1 class="doc-title">{{ workspace.documents[pane.activeDocumentId].title }}</h1>
                <component
                  :is="canOpenHistory(pane.activeDocumentId) ? 'button' : 'div'"
                  :type="canOpenHistory(pane.activeDocumentId) ? 'button' : undefined"
                  class="doc-meta"
                  :class="{ 'doc-meta-trigger': canOpenHistory(pane.activeDocumentId) }"
                  :title="canOpenHistory(pane.activeDocumentId) ? 'View edit history' : undefined"
                  @click="canOpenHistory(pane.activeDocumentId) && openHistory(pane.activeDocumentId)"
                >
                  <UserAvatar :name="workspace.documents[pane.activeDocumentId].lastModifiedByUserName" size="small" aria-hidden="true" />
                  <span>Edited by {{ workspace.documents[pane.activeDocumentId].lastModifiedByUserName }} · {{ savedRelative(workspace.documents[pane.activeDocumentId].savedAt) }}</span>
                  <History v-if="canOpenHistory(pane.activeDocumentId)" class="icon doc-meta-history-icon" />
                </component>
              </div>
              <span
                v-if="showSaveBadge(pane.activeDocumentId)"
                :class="['save-pill', 'save-pill-round', workspace.getSaveStateClass(pane.activeDocumentId), { clickable: workspace.canRetrySave(pane.activeDocumentId) }]"
                :title="workspace.getSaveState(pane.activeDocumentId)"
                @click="workspace.canRetrySave(pane.activeDocumentId) && void workspace.retrySaveDocument(pane.activeDocumentId)"
              >
                <RefreshCw v-if="isSaving(pane)" class="icon save-pill-spin" />
                <AlertTriangle v-else-if="isSaveAlert(pane)" class="icon" />
                <CircleDot v-else-if="isDirty(pane)" class="icon" />
                <Check v-else class="icon" />
              </span>
            </header>
            <div class="pane-editor-body">
              <MarkdownEditor
                :document-id="pane.activeDocumentId"
                :pane-index="pane.paneIndex"
                :content-markdown="workspace.getDraft(pane.activeDocumentId)"
                :can-edit="workspace.documents[pane.activeDocumentId].canEdit"
                :initial-viewport-position="getInitialViewportPosition(pane.paneIndex, pane.activeDocumentId)"
                :initial-collapsed-heading-positions="getCollapsedHeadingPositions(pane.paneIndex, pane.activeDocumentId)"
                :readable-diff-baseline-markdown="getReadableDiffBaseline(pane.activeDocumentId)"
                @change="content => pane.activeDocumentId != null && void workspace.setDraft(pane.activeDocumentId, content)"
                @viewport-change="(documentId, position) => saveEditorViewportPosition(pane.paneIndex, documentId, position)"
                @collapsed-heading-positions-change="(documentId, positions) => saveCollapsedHeadingPositions(pane.paneIndex, documentId, positions)"
                @readable-diff-cleared="clearReadableDiff"
              />
            </div>
          </template>
          <div v-else-if="pane.activeDocumentId" class="pane-empty">Loading document</div>
          <div v-else class="pane-empty">Select a document from the sidebar</div>
        </div>
      </section>
      </div>
      <div
        v-if="commentsPanelOpen && commentsPanelDocument"
        class="comments-panel-column"
        :class="{ resizing: commentsPanelResizePointerId != null }"
        :style="{ '--comments-user-width': `${commentsPanelWidth}px` }"
      >
        <DocumentCommentsPanel
          :document="commentsPanelDocument"
          :active-diff-comment-id="activeDiffCommentId"
          @close="closeCommentsPanel"
          @show-changes="showCommentChanges"
          @hide-changes="clearReadableDiff"
        />
        <div
          class="comments-panel-resizer"
          role="separator"
          aria-label="Resize comments panel"
          aria-orientation="vertical"
          :aria-valuemin="minimumCommentsPanelWidth"
          :aria-valuemax="maximumCommentsPanelWidth"
          :aria-valuenow="commentsPanelWidth"
          tabindex="0"
          @keydown="resizeCommentsPanelWithKeyboard"
          @pointerdown="beginCommentsPanelResize"
          @pointermove="resizeCommentsPanel"
          @pointerup="finishCommentsPanelResize"
          @pointercancel="finishCommentsPanelResize"
        ></div>
      </div>
    </div>
  </section>
  <section v-else class="workspace">
    <div class="workspace-empty">Loading workspace</div>
  </section>
  <DocumentSaveConflictDialog
    v-if="workspace.activeSaveConflict"
    :conflict="workspace.activeSaveConflict"
    @overwrite="overwriteActiveConflict"
    @rollback="rollbackActiveConflict"
  />
  <DocumentHistoryDialog
    v-if="historyDialogDocumentId && workspace.documents[historyDialogDocumentId]"
    :document="workspace.documents[historyDialogDocumentId]"
    @close="historyDialogDocumentId = null"
    @show-changes="showHistoryChanges"
    @restored="onHistoryRestored"
  />
</template>

<script setup lang="ts">
import { AlertTriangle, Check, CircleDot, Columns2, FileText, History, MessageSquare, PanelLeft, PanelRightClose, RefreshCw, X } from 'lucide-vue-next';
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ApiError } from '../api/client';
import type { GetDocumentQueryResponse } from '../api/generated';
import type { EntityId } from '../api/entityId';
import DocumentCommentsPanel from '../components/DocumentCommentsPanel.vue';
import DocumentHistoryDialog from '../components/DocumentHistoryDialog.vue';
import DocumentSaveConflictDialog from '../components/DocumentSaveConflictDialog.vue';
import MarkdownEditor from '../components/MarkdownEditor.vue';
import { getDraggedDocumentId, isDraggedDocument } from '../dragDrop';
import UserAvatar from '../components/UserAvatar.vue';
import { sidebarControlsKey, type SidebarControls } from '../sidebarControls';
import { getErrorMessage, useSessionStore } from '../stores/session';
import { paneClass, useWorkspaceStore } from '../stores/workspace';
import {
  type EditorPaneState,
  getCommentsPanelOpen,
  getCommentsPanelWidth,
  getEditorViewportPosition,
  setCommentsPanelOpen,
  setCommentsPanelWidth,
  setEditorViewportPosition,
  type EditorViewportPosition
} from '../stores/workspaceViewport';

const session = useSessionStore();
const workspace = useWorkspaceStore();
const route = useRoute();
const router = useRouter();
const dragOverPaneIndex = ref<number | null>(null);
const historyDialogDocumentId = ref<EntityId | null>(null);
const commentsPanelOpen = ref(false);
type ReadableDiffState = {
  source: 'comment';
  documentId: EntityId;
  commentId: EntityId;
  baselineContentMarkdown: string;
} | {
  source: 'history';
  documentId: EntityId;
  historyEntryId: EntityId;
  baselineContentMarkdown: string;
};

const readableDiff = ref<ReadableDiffState | null>(null);
const collapsedHeadingPositions = new Map<number, Map<EntityId, number[]>>();
const sidebar = inject(sidebarControlsKey) as SidebarControls;
const defaultCommentsPanelWidth = 320;
const minimumCommentsPanelWidth = 260;
const commentsPanelResizeBreakpoint = 1100;
const commentsPanelKeyboardStep = 16;
const commentsPanelWidth = ref(defaultCommentsPanelWidth);
const maximumCommentsPanelWidth = ref(calculateMaximumCommentsPanelWidth());
const commentsPanelResizePointerId = ref<number | null>(null);
let commentsPanelResizeStartX = 0;
let commentsPanelResizeStartWidth = defaultCommentsPanelWidth;

const currentError = computed(() => workspace.error || session.error);
const editorGridClass = computed(() => (workspace.visiblePanes.length > 1 ? 'editor-grid panes-2' : 'editor-grid'));
const activeDiffCommentId = computed(() =>
  readableDiff.value?.source === 'comment' ? readableDiff.value.commentId : null);

onMounted(async () => {
  window.addEventListener('resize', updateCommentsPanelWidthLimit);
  updateCommentsPanelWidthLimit();
  await initializeWorkspace();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateCommentsPanelWidthLimit);
});

watch(
  () => session.profile?.id,
  userId => {
    commentsPanelWidth.value = clampCommentsPanelWidth(
      userId == null ? defaultCommentsPanelWidth : getCommentsPanelWidth(userId) ?? defaultCommentsPanelWidth
    );
    commentsPanelOpen.value = userId != null && getCommentsPanelOpen(userId) === true;
  },
  { immediate: true }
);

watch(
  () => session.isAuthenticated,
  async () => {
    await initializeWorkspace();
  }
);

watch(
  () => route.params.documentId,
  async () => {
    await initializeWorkspace();
  }
);

async function initializeWorkspace(): Promise<void> {
  if (!session.isAuthenticated) {
    return;
  }

  try {
    await applyWorkspaceBootstrap();
    await loadRouteDocument();
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to load workspace.');
  }
}

async function applyWorkspaceBootstrap(force = false): Promise<void> {
  if (session.pageBootstrap?.workspace != null && (force || !workspace.workspace)) {
    await workspace.applyBootstrap(session.pageBootstrap);
    return;
  }

  if (workspace.workspace) {
    return;
  }

  await workspace.loadWorkspace();
}

async function loadRouteDocument(): Promise<void> {
  if (!workspace.workspace) {
    return;
  }

  const documentId = routeDocumentId();
  if (documentId != null) {
    if (!workspace.hasDocumentInWorkspace(documentId)) {
      await workspace.loadWorkspace();
      if (!workspace.hasDocumentInWorkspace(documentId)) {
        await syncRouteWithActiveDocument();
        return;
      }
    }

    try {
      await workspace.openDocument(documentId);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        await workspace.loadWorkspace();
        await syncRouteWithActiveDocument();
        return;
      }

      workspace.error = getErrorMessage(error, 'Unable to load document.');
    }
  }
}

async function activateDocument(paneIndex: number, documentId: EntityId): Promise<void> {
  try {
    await workspace.activateDocument(paneIndex, documentId);
    await syncRouteWithActiveDocument();
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to activate document.');
  }
}

async function activatePane(paneIndex: number): Promise<void> {
  await workspace.setActivePane(paneIndex);
  await syncRouteWithActiveDocument();
}

async function closeTabOnMiddleClick(event: MouseEvent, paneIndex: number, documentId: EntityId): Promise<void> {
  if (event.button === 1) {
    await closeDocument(paneIndex, documentId);
  }
}

async function closeDocument(paneIndex: number, documentId: EntityId): Promise<void> {
  await workspace.closeDocument(paneIndex, documentId);
  await syncRouteWithActiveDocument();
}

async function onDocumentRestored(document: GetDocumentQueryResponse): Promise<void> {
  const documentId = String(document.id);
  await workspace.discardLocalDraft(documentId);
  await workspace.applyLoadedDocument(document);
}

function canOpenHistory(documentId: EntityId): boolean {
  return workspace.documents[documentId]?.canViewHistory === true;
}

function openHistory(documentId: EntityId): void {
  historyDialogDocumentId.value = documentId;
}

async function onHistoryRestored(document: GetDocumentQueryResponse): Promise<void> {
  historyDialogDocumentId.value = null;
  await onDocumentRestored(document);
}

async function overwriteActiveConflict(): Promise<void> {
  const conflict = workspace.activeSaveConflict;
  if (conflict == null) {
    throw new Error('Document save conflict was not found.');
  }

  await workspace.overwriteSaveConflict(conflict.documentId);
}

async function rollbackActiveConflict(): Promise<void> {
  const conflict = workspace.activeSaveConflict;
  if (conflict == null) {
    throw new Error('Document save conflict was not found.');
  }

  await workspace.rollbackSaveConflict(conflict.documentId);
}

function paneCssClass(pane: EditorPaneState): string {
  const cssClass = paneClass(pane, workspace.editorState.activePaneIndex, workspace.visiblePanes.length);
  return dragOverPaneIndex.value === pane.paneIndex ? `${cssClass} drag-over` : cssClass;
}

function tabClass(pane: EditorPaneState, documentId: EntityId): string {
  return pane.activeDocumentId === documentId ? 'pane-tab active' : 'pane-tab';
}

function activeDoc(pane: EditorPaneState) {
  return pane.activeDocumentId != null ? workspace.documents[pane.activeDocumentId] ?? null : null;
}

function saveStateKind(pane: EditorPaneState): string {
  return pane.activeDocumentId != null ? workspace.getSaveStateClass(pane.activeDocumentId) : 'saved';
}

function isSaving(pane: EditorPaneState): boolean {
  return saveStateKind(pane) === 'saving';
}

function isSaveAlert(pane: EditorPaneState): boolean {
  return ['retrying', 'blocked', 'conflict'].includes(saveStateKind(pane));
}

function isDirty(pane: EditorPaneState): boolean {
  return saveStateKind(pane) === 'dirty';
}

const savedFlash = reactive<Record<EntityId, boolean>>({});
const savedFlashTimers: Record<EntityId, number> = {};
const previousSaveKind: Record<EntityId, string> = {};

const activeSaveKinds = computed<Record<EntityId, string>>(() => {
  const map: Record<EntityId, string> = {};
  for (const pane of workspace.visiblePanes) {
    if (pane.activeDocumentId != null) {
      map[pane.activeDocumentId] = workspace.getSaveStateClass(pane.activeDocumentId);
    }
  }

  return map;
});

watch(activeSaveKinds, kinds => {
  for (const [documentId, kind] of Object.entries(kinds)) {
    const previous = previousSaveKind[documentId];
    if (kind === 'saved' && previous != null && previous !== 'saved') {
      flashSaved(documentId);
    }

    previousSaveKind[documentId] = kind;
  }
}, { deep: true, immediate: true });

function flashSaved(documentId: EntityId): void {
  savedFlash[documentId] = true;
  if (savedFlashTimers[documentId] != null) {
    window.clearTimeout(savedFlashTimers[documentId]);
  }

  savedFlashTimers[documentId] = window.setTimeout(() => {
    savedFlash[documentId] = false;
    delete savedFlashTimers[documentId];
  }, 3000);
}

function showSaveBadge(documentId: EntityId): boolean {
  return workspace.getSaveStateClass(documentId) !== 'saved' || savedFlash[documentId] === true;
}

const splitTitle = computed(() => (workspace.visiblePanes.length > 1 ? 'Close right pane' : 'Open second pane'));

function showSplitButton(pane: EditorPaneState): boolean {
  return workspace.visiblePanes.length === 1 || pane.paneIndex === 1;
}

function showCommentsButton(pane: EditorPaneState): boolean {
  const document = activeDoc(pane);
  return workspace.visiblePanes.length === 1 && document?.canViewComments === true;
}

function toggleCommentsPanel(pane: EditorPaneState): void {
  if (!commentsPanelOpen.value && !showCommentsButton(pane)) {
    return;
  }

  commentsPanelOpen.value = !commentsPanelOpen.value;
}

function closeCommentsPanel(): void {
  clearCommentReadableDiff();
  commentsPanelOpen.value = false;
}

function showCommentChanges(commentId: EntityId, baselineContentMarkdown: string): void {
  const documentId = commentsPanelDocument.value?.id;
  if (!commentsPanelOpen.value || documentId == null) {
    return;
  }

  readableDiff.value = { source: 'comment', documentId, commentId, baselineContentMarkdown };
}

function showHistoryChanges(
  documentId: EntityId,
  historyEntryId: EntityId,
  baselineContentMarkdown: string
): void {
  readableDiff.value = { source: 'history', documentId, historyEntryId, baselineContentMarkdown };
  historyDialogDocumentId.value = null;
}

function clearReadableDiff(): void {
  readableDiff.value = null;
}

function clearCommentReadableDiff(): void {
  if (readableDiff.value?.source === 'comment') {
    clearReadableDiff();
  }
}

function getReadableDiffBaseline(documentId: EntityId): string | null {
  return readableDiff.value?.documentId === documentId
    ? readableDiff.value.baselineContentMarkdown
    : null;
}

const commentsPanelDocument = computed<GetDocumentQueryResponse | null>(() => {
  if (!commentsPanelOpen.value) {
    return null;
  }

  const documentId = workspace.activeDocumentId;
  if (documentId == null) {
    return null;
  }

  const document = workspace.documents[documentId];
  return document?.canViewComments === true ? document : null;
});

watch(commentsPanelOpen, open => {
  if (!open) {
    clearCommentReadableDiff();
  }

  const userId = session.profile?.id;
  if (userId != null) {
    setCommentsPanelOpen(userId, open);
  }
});

watch(
  () => workspace.activeDocumentId,
  () => {
    clearReadableDiff();
  }
);

watch(
  () => workspace.visiblePanes.length,
  paneCount => {
    if (paneCount > 1) {
      commentsPanelOpen.value = false;
    }
  }
);

function beginCommentsPanelResize(event: PointerEvent): void {
  if (event.button !== 0 || window.innerWidth <= commentsPanelResizeBreakpoint) {
    return;
  }

  commentsPanelResizePointerId.value = event.pointerId;
  commentsPanelResizeStartX = event.clientX;
  commentsPanelResizeStartWidth = commentsPanelWidth.value;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
}

function resizeCommentsPanel(event: PointerEvent): void {
  if (commentsPanelResizePointerId.value !== event.pointerId) {
    return;
  }

  commentsPanelWidth.value = clampCommentsPanelWidth(
    commentsPanelResizeStartWidth - (event.clientX - commentsPanelResizeStartX)
  );
}

function finishCommentsPanelResize(event: PointerEvent): void {
  if (commentsPanelResizePointerId.value !== event.pointerId) {
    return;
  }

  const resizer = event.currentTarget as HTMLElement;
  if (resizer.hasPointerCapture(event.pointerId)) {
    resizer.releasePointerCapture(event.pointerId);
  }

  commentsPanelResizePointerId.value = null;
  persistCommentsPanelWidth();
}

function resizeCommentsPanelWithKeyboard(event: KeyboardEvent): void {
  if (window.innerWidth <= commentsPanelResizeBreakpoint
    || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
    return;
  }

  event.preventDefault();
  const direction = event.key === 'ArrowLeft' ? 1 : -1;
  commentsPanelWidth.value = clampCommentsPanelWidth(commentsPanelWidth.value + direction * commentsPanelKeyboardStep);
  persistCommentsPanelWidth();
}

function persistCommentsPanelWidth(): void {
  const userId = session.profile?.id;
  if (userId != null) {
    setCommentsPanelWidth(userId, commentsPanelWidth.value);
  }
}

function updateCommentsPanelWidthLimit(): void {
  maximumCommentsPanelWidth.value = calculateMaximumCommentsPanelWidth();
  commentsPanelWidth.value = clampCommentsPanelWidth(commentsPanelWidth.value);
}

function clampCommentsPanelWidth(width: number): number {
  return Math.min(maximumCommentsPanelWidth.value, Math.max(minimumCommentsPanelWidth, Math.round(width)));
}

function calculateMaximumCommentsPanelWidth(): number {
  return Math.max(minimumCommentsPanelWidth, Math.floor(window.innerWidth / 2));
}

async function toggleSplit(pane: EditorPaneState): Promise<void> {
  if (workspace.visiblePanes.length > 1) {
    const secondary = workspace.visiblePanes.find(candidate => candidate.paneIndex === 1);
    for (const tab of secondary?.tabs ?? []) {
      await workspace.closeDocument(1, tab.documentId);
    }

    await syncRouteWithActiveDocument();
    return;
  }

  if (pane.activeDocumentId != null) {
    const targetPaneIndex = pane.paneIndex === 0 ? 1 : 0;
    await workspace.openDocument(pane.activeDocumentId, targetPaneIndex);
  }
}

function savedRelative(instant: unknown): string {
  if (typeof instant !== 'string' && typeof instant !== 'number') {
    return '';
  }

  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 45) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }

  return date.toLocaleDateString();
}

function getInitialViewportPosition(paneIndex: number, documentId: EntityId | null | undefined): EditorViewportPosition | null {
  const userId = session.profile?.id;
  if (userId == null || documentId == null) {
    return null;
  }

  return getEditorViewportPosition(userId, paneIndex, documentId);
}

function saveEditorViewportPosition(
  paneIndex: number,
  documentId: EntityId,
  position: EditorViewportPosition
): void {
  const userId = session.profile?.id;
  if (userId == null) {
    return;
  }

  setEditorViewportPosition(userId, paneIndex, documentId, position);
}

function getCollapsedHeadingPositions(paneIndex: number, documentId: EntityId): number[] {
  return collapsedHeadingPositions.get(paneIndex)?.get(documentId) ?? [];
}

function saveCollapsedHeadingPositions(paneIndex: number, documentId: EntityId, positions: number[]): void {
  if (positions.length === 0) {
    collapsedHeadingPositions.get(paneIndex)?.delete(documentId);
    return;
  }

  let panePositions = collapsedHeadingPositions.get(paneIndex);
  if (panePositions == null) {
    panePositions = new Map<EntityId, number[]>();
    collapsedHeadingPositions.set(paneIndex, panePositions);
  }

  panePositions.set(documentId, positions);
}

function dragEnterPane(paneIndex: number, event: DragEvent): void {
  if (!isDraggedDocument(event)) {
    return;
  }

  event.preventDefault();
  dragOverPaneIndex.value = paneIndex;
}

function dragOverPane(paneIndex: number, event: DragEvent): void {
  if (!isDraggedDocument(event)) {
    return;
  }

  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'copy';
  }

  dragOverPaneIndex.value = paneIndex;
}

function dragLeavePane(paneIndex: number, event: DragEvent): void {
  if (event.currentTarget instanceof HTMLElement
    && event.relatedTarget instanceof Node
    && event.currentTarget.contains(event.relatedTarget)) {
    return;
  }

  if (dragOverPaneIndex.value === paneIndex) {
    dragOverPaneIndex.value = null;
  }
}

async function dropDocumentOnPane(paneIndex: number, event: DragEvent): Promise<void> {
  const documentId = getDraggedDocumentId(event);
  if (documentId == null) {
    return;
  }

  event.preventDefault();
  dragOverPaneIndex.value = null;
  try {
    await workspace.setActivePane(paneIndex);
    await router.push({ name: 'workspace-document', params: { documentId: String(documentId) } });
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to open document.');
  }
}

async function syncRouteWithActiveDocument(): Promise<void> {
  const documentId = workspace.activeDocumentId;
  if (documentId == null) {
    if (routeDocumentId() != null) {
      await router.push('/');
    }

    return;
  }

  if (routeDocumentId() !== documentId) {
    await router.push({ name: 'workspace-document', params: { documentId } });
  }
}

function routeDocumentId(): EntityId | null {
  const value = route.params.documentId;
  const candidate = Array.isArray(value) ? value[0] : value;
  return typeof candidate === 'string' && /^\d+$/.test(candidate) ? candidate : null;
}
</script>
